import {
  type AdvertiserAudienceAb,
  type AudienceAb,
  type LookalikeAudienceAb,
  type RuleBasedAudienceAb,
  RuleBasedAudienceCombinatorOperator,
  RuleBasedAudienceCondition,
  type RuleBasedAudienceFilters,
  RuleBasedAudienceFiltersBooleanOperator,
} from "features/mediaDataRoom/models";
import type * as raw from "./models";

const mapFilterOperatorToAudienceCondition: Record<
  raw.FilterOperator,
  RuleBasedAudienceCondition
> = {
  contains_all_of: RuleBasedAudienceCondition.CONTAINS_ALL_OF,
  contains_any_of: RuleBasedAudienceCondition.CONTAINS_ANY_OF,
  contains_none_of: RuleBasedAudienceCondition.CONTAINS_NONE_OF,
  empty: RuleBasedAudienceCondition.EMPTY,
  not_empty: RuleBasedAudienceCondition.NOT_EMPTY,
};

const mapCombineOperatorToAudienceCombinatorOperator: Record<
  raw.CombineOperator,
  RuleBasedAudienceCombinatorOperator
> = {
  diff: RuleBasedAudienceCombinatorOperator.DIFF,
  intersect: RuleBasedAudienceCombinatorOperator.INTERSECT,
  union: RuleBasedAudienceCombinatorOperator.UNION,
};

const mapBooleanOpToAudienceFiltersOperator: Record<
  raw.BooleanOp,
  RuleBasedAudienceFiltersBooleanOperator
> = {
  and: RuleBasedAudienceFiltersBooleanOperator.AND,
  or: RuleBasedAudienceFiltersBooleanOperator.OR,
};

const mapDdcAudienceFiltersToAudienceFilters = (
  filters: raw.AudienceFilters
): RuleBasedAudienceFilters => ({
  filters: filters.filters.map(({ attribute, operator, values }) => ({
    attribute,
    condition: mapFilterOperatorToAudienceCondition[operator],
    values: values ?? null,
  })),
  operator: mapBooleanOpToAudienceFiltersOperator[filters.boolean_op],
});

export const mapRawAudienceToAudienceAb = (
  rawAudience: raw.Audience
): AudienceAb => {
  switch (rawAudience.kind) {
    case "advertiser": {
      const audience: AdvertiserAudienceAb = {
        createdAt: rawAudience.mutable.created_at ?? undefined,
        id: rawAudience.id,
        isPublishedAsIntermediate:
          rawAudience.mutable.status === "published_as_intermediate",
        kind: "advertiser",
        name: rawAudience.mutable.name,
        size: rawAudience.audience_size ?? undefined,
        status:
          rawAudience.mutable.status === "published" ||
          rawAudience.mutable.status === "published_as_intermediate"
            ? "published"
            : "ready",
        type: rawAudience.audience_type,
      };
      return audience;
    }
    case "lookalike": {
      const audience: LookalikeAudienceAb = {
        createdAt: rawAudience.mutable.created_at ?? undefined,
        excludeSeedAudience: rawAudience.exclude_seed_audience ?? false,
        id: rawAudience.id,
        isPublishedAsIntermediate:
          rawAudience.mutable.status === "published_as_intermediate",
        kind: "lookalike",
        name: rawAudience.mutable.name,
        reach: rawAudience.reach,
        sourceRef: rawAudience.source_ref,
        status:
          rawAudience.mutable.status === "published" ||
          rawAudience.mutable.status === "published_as_intermediate"
            ? "published"
            : "ready",
      };
      return audience;
    }
    case "rulebased": {
      const audience: RuleBasedAudienceAb = {
        combine: rawAudience.combine
          ? rawAudience.combine.map(({ filters, source_ref, operator }) => ({
              filters: filters
                ? mapDdcAudienceFiltersToAudienceFilters(filters)
                : null,
              operator:
                mapCombineOperatorToAudienceCombinatorOperator[operator],
              sourceRef: source_ref,
            }))
          : null,
        createdAt: rawAudience.mutable.created_at ?? undefined,
        filters: rawAudience.filters
          ? mapDdcAudienceFiltersToAudienceFilters(rawAudience.filters)
          : null,
        id: rawAudience.id,
        isPublishedAsIntermediate:
          rawAudience.mutable.status === "published_as_intermediate",
        kind: "rulebased",
        name: rawAudience.mutable.name,
        sourceRef: rawAudience.source_ref,
        status:
          rawAudience.mutable.status === "published" ||
          rawAudience.mutable.status === "published_as_intermediate"
            ? "published"
            : "ready",
      };
      return audience;
    }
  }
};

const mapAudienceFiltersOperatorToBooleanOp: Record<
  RuleBasedAudienceFiltersBooleanOperator,
  raw.BooleanOp
> = {
  [RuleBasedAudienceFiltersBooleanOperator.AND]: "and",
  [RuleBasedAudienceFiltersBooleanOperator.OR]: "or",
};

const mapAudienceConditionToFilterOperator: Record<
  RuleBasedAudienceCondition,
  raw.FilterOperator
> = {
  [RuleBasedAudienceCondition.CONTAINS_ANY_OF]: "contains_any_of",
  [RuleBasedAudienceCondition.CONTAINS_NONE_OF]: "contains_none_of",
  [RuleBasedAudienceCondition.CONTAINS_ALL_OF]: "contains_all_of",
  [RuleBasedAudienceCondition.EMPTY]: "empty",
  [RuleBasedAudienceCondition.NOT_EMPTY]: "not_empty",
};

const mapAudienceCombinatorOperatorToCombineOperator: Record<
  RuleBasedAudienceCombinatorOperator,
  raw.CombineOperator
> = {
  [RuleBasedAudienceCombinatorOperator.INTERSECT]: "intersect",
  [RuleBasedAudienceCombinatorOperator.UNION]: "union",
  [RuleBasedAudienceCombinatorOperator.DIFF]: "diff",
};

const mapAudienceFiltersToDdcAudienceFilters = (
  filters: RuleBasedAudienceFilters
): raw.AudienceFilters => ({
  boolean_op: mapAudienceFiltersOperatorToBooleanOp[filters.operator],
  filters: filters.filters.map(({ condition, attribute, values }) => ({
    attribute: attribute ?? "",
    operator:
      mapAudienceConditionToFilterOperator[
        condition ?? RuleBasedAudienceCondition.CONTAINS_ANY_OF
      ],
    values: values ?? undefined,
  })),
});

export const mapAudienceAbToRawAudience = (
  audience: AudienceAb
): raw.Audience => {
  switch (audience.kind) {
    case "advertiser": {
      const rawAudience: raw.AdvertiserAudience = {
        audience_size: audience.size,
        audience_type: audience.type,
        id: audience.id,
        kind: "advertiser",
        mutable: {
          created_at: audience.createdAt,
          name: audience.name,
          status: audience.isPublishedAsIntermediate
            ? "published_as_intermediate"
            : audience.status,
        },
      };
      return rawAudience;
    }
    case "lookalike": {
      const rawAudience: raw.LookalikeAudience = {
        exclude_seed_audience: audience.excludeSeedAudience,
        id: audience.id,
        kind: "lookalike",
        mutable: {
          created_at: audience.createdAt,
          name: audience.name,
          status: audience.isPublishedAsIntermediate
            ? "published_as_intermediate"
            : audience.status,
        },
        reach: audience.reach,
        source_ref: audience.sourceRef,
      };
      return rawAudience;
    }
    case "rulebased": {
      const rawAudience: raw.RuleBasedAudience = {
        combine: audience.combine
          ? audience.combine.map(({ filters, operator, sourceRef }) => ({
              filters: filters
                ? mapAudienceFiltersToDdcAudienceFilters(filters)
                : undefined,
              operator:
                mapAudienceCombinatorOperatorToCombineOperator[operator],
              source_ref: sourceRef,
            }))
          : undefined,
        filters: audience.filters
          ? mapAudienceFiltersToDdcAudienceFilters(audience.filters)
          : undefined,
        id: audience.id,
        kind: "rulebased",
        mutable: {
          created_at: audience.createdAt,
          name: audience.name,
          status: audience.isPublishedAsIntermediate
            ? "published_as_intermediate"
            : audience.status,
        },
        source_ref: audience.sourceRef,
      };
      return rawAudience;
    }
  }
};
